/**
 * Generic pillar landing page for the Notches preview namespace.
 *
 * Lives at /notches/dashboard/{signals|markets|reports|auto-traders}.
 * Each pillar's route renders this component with a pillar key prop. The
 * page shows just that pillar's tiles (active + locked) and links back to
 * the dashboard.
 *
 * Gated by notches.preview entitlement. New surface; nothing in the existing
 * system is touched.
 */
import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { hasSubscription } from '../../lib/entitlements';
import { ENTITLEMENTS, EntitlementKey } from '../../constants/entitlements';

type Pillar = 'signals' | 'markets' | 'reports' | 'auto-traders';

const c = {
  bg: '#0f172a', card: '#1f2937', cardAlt: '#1a2332', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
};

// Same tile catalog the Dashboard uses. Imported lazily here so both pages
// stay in sync without one importing from the other (loose coupling - this
// constant can be hoisted to a shared module later if a third consumer
// appears). For now duplicated intentionally.
interface SubAction { label: string; to: string; }
interface Tile {
  // Any of these entitlements unlocks the tile (per-symbol bundles supply both).
  keys: EntitlementKey[];
  label: string;
  blurb: string;
  teaser: string;
  to: string;
  subActions?: SubAction[];
  alwaysActive?: boolean;
  pillar: Pillar;
  accent: string;
}

const TILES: Tile[] = [
  { keys: [ENTITLEMENTS.SIGNALS_OPTIONS_SPX, ENTITLEMENTS.SIGNALS_OPTIONS_SPY], label: 'Options Research - Index',   blurb: 'SPX & SPY candidate picks, scored across all DTEs.', teaser: 'Daily-graded options trades on SPX & SPY with AI-narrated reasoning + skew/macro context. Each symbol is a separate purchase.', to: '/notches?group=index', pillar: 'signals', accent: '#10b981' },
  { keys: [ENTITLEMENTS.SIGNALS_OPTIONS_ES,  ENTITLEMENTS.SIGNALS_OPTIONS_MES], label: 'Options Research - Futures', blurb: 'ES & MES candidate picks for futures-options sellers.', teaser: 'Futures-options income with 23-hour markets; same scoring model as Index Options. Each symbol is a separate purchase.', to: '/notches?group=futures', pillar: 'signals', accent: '#10b981' },
  { keys: [ENTITLEMENTS.SIGNALS_OPTIONS_EQUITY], label: 'Options Research - Equity',  blurb: 'Single-stock options picks on liquid US equities.', teaser: 'Per-name options-income candidates on AAPL, NVDA, TSLA and other liquid US equities.', to: '/notches/signals/options/equity', pillar: 'signals', accent: '#10b981' },
  { keys: [ENTITLEMENTS.SIGNALS_STOCKS],         label: 'Stock Swing Research',       blurb: 'Multi-day swing setups on liquid US equities.', teaser: 'Multi-factor swing trades emailed daily; verified win-rate banner; clear entries + exits.', to: '/notches/signals/stocks', pillar: 'signals', accent: '#10b981' },
  { keys: [ENTITLEMENTS.MARKETS],                label: 'Market Commentary',         blurb: 'Daily morning research + voice notes from the WFS desk.', teaser: 'Plain-English daily reads on what moved + what to watch; not for the screen-watchers.', to: '/markets', pillar: 'markets', accent: '#3b82f6' },
  { keys: [ENTITLEMENTS.AUTO_TRADERS_TASTYTRADE], label: 'TastyTrade Auto-Trader',   blurb: 'Hands-off options-income engines on your TT account.', teaser: 'Configure preset + risk; the engine opens / monitors / closes spreads on your TT account.', to: '/notches/auto-traders/tastytrade', subActions: [ { label: 'Trades', to: '/notches/auto-traders/tastytrade/trades' }, { label: 'Analytics', to: '/notches/auto-traders/tastytrade/analytics' }, { label: 'Audit', to: '/notches/auto-traders/tastytrade/audit' } ], pillar: 'auto-traders', accent: '#8b5cf6' },
  { keys: [ENTITLEMENTS.AUTO_TRADERS_ALPACA],     label: 'Alpaca Auto-Trader',       blurb: 'Stock-research-driven trades on your Alpaca account.', teaser: 'Wakes on every stock pick; sizes per your risk; fully audit-logged.', to: '/notches/auto-traders/alpaca', subActions: [ { label: 'Signals', to: '/notches/auto-traders/alpaca/signals' }, { label: 'Trades', to: '/notches/auto-traders/alpaca/trades' }, { label: 'Portfolio', to: '/notches/auto-traders/alpaca/portfolio' }, { label: 'Audit', to: '/notches/auto-traders/alpaca/audit' } ], pillar: 'auto-traders', accent: '#8b5cf6' },
];

const PILLAR_META: Record<Pillar, { label: string; color: string; tagline: string }> = {
  signals:       { label: 'Research',      color: '#10b981', tagline: 'Trade ideas scored and ranked daily across asset classes.' },
  markets:       { label: 'Markets',       color: '#3b82f6', tagline: 'Editorial and voice commentary from the WFS desk.' },
  reports:       { label: 'Reports',       color: '#f59e0b', tagline: 'Scheduled and on-demand reports for your watchlist.' },
  'auto-traders':{ label: 'Auto-Traders',  color: '#8b5cf6', tagline: 'Hands-off engines that act on picks automatically.' },
};

export const PillarLanding: React.FC<{ pillar: Pillar }> = ({ pillar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!hasSubscription(user, ENTITLEMENTS.NOTCHES_PREVIEW)) {
    return <Navigate to="/notches" replace />;
  }

  const meta = PILLAR_META[pillar];
  const tiles = TILES.filter((t) => t.pillar === pillar);
  // alwaysActive tiles (free products) bypass the gate. Otherwise: any of the
  // tile's listed entitlements unlocks it (per-symbol bundles like Index check both).
  const tileHasAccess = (t: Tile) => t.alwaysActive || t.keys.some((k) => hasSubscription(user, k));
  const active = tiles.filter(tileHasAccess);
  const locked = tiles.filter((t) => !tileHasAccess(t));

  return (
    <div className="dashboard-container" style={{ background: c.bg, minHeight: '100vh', color: c.text }}>
      <Navigation />
      <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ fontSize: 12, marginBottom: 10 }}>
          <Link to="/notches/dashboard" style={{ color: c.muted, textDecoration: 'none' }}>Notches</Link>
          <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
          <span style={{ color: c.text }}>{meta.label}</span>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          marginBottom: 6, paddingBottom: 10,
          borderBottom: `2px solid ${meta.color}`,
        }}>
          <span style={{ width: 8, height: 28, borderRadius: 2, background: meta.color }} />
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>{meta.label}</h1>
          <span style={{ fontSize: 12, color: c.muted }}>
            {active.length} active &middot; {locked.length} available
          </span>
        </div>
        <p style={{ color: c.muted, fontSize: 14, marginTop: 12, marginBottom: 24 }}>
          {meta.tagline}
        </p>

        {tiles.length === 0 && (
          <div style={{
            background: c.card, border: `1px dashed ${c.border}`,
            borderRadius: 8, padding: 24, textAlign: 'center', color: c.muted,
          }}>
            Nothing yet under {meta.label}. Coming soon.
          </div>
        )}

        {active.length > 0 && (
          <>
            <h2 style={{ fontSize: 13, color: c.muted, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', margin: '0 0 10px' }}>
              Your services
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12, marginBottom: 28,
            }}>
              {active.map((t) => (
                // div + role=button so the sub-action chips below can be their
                // own interactive elements (nested interactives inside a real
                // <button> are invalid HTML).
                <div
                  key={t.label}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(t.to)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); navigate(t.to);
                    }
                  }}
                  style={{
                    background: c.card, border: `1px solid ${c.border}`,
                    borderLeft: `3px solid ${t.accent}`, borderRadius: 8,
                    padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
                    color: c.text, display: 'flex', flexDirection: 'column', gap: 6,
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = c.cardAlt; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = c.card; }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{t.label}</span>
                  <span style={{ fontSize: 12, color: c.muted, lineHeight: 1.4 }}>{t.blurb}</span>
                  <span style={{ fontSize: 11, color: t.accent, marginTop: 4 }}>Open &rarr;</span>
                  {t.subActions && t.subActions.length > 0 && (
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4,
                      paddingTop: 8, borderTop: `1px dashed ${c.border}`,
                    }}>
                      {t.subActions.map((a) => (
                        <span
                          key={a.to}
                          role="link"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); navigate(a.to); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault(); e.stopPropagation(); navigate(a.to);
                            }
                          }}
                          style={{
                            fontSize: 11, color: c.muted, padding: '2px 8px',
                            border: `1px solid ${c.border}`, borderRadius: 10,
                            background: 'transparent', cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.color = t.accent;
                            (e.currentTarget as HTMLElement).style.borderColor = t.accent;
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.color = c.muted;
                            (e.currentTarget as HTMLElement).style.borderColor = c.border;
                          }}
                        >{a.label}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {locked.length > 0 && (
          <>
            <h2 style={{ fontSize: 13, color: c.muted, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', margin: '0 0 10px' }}>
              Available to add
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}>
              {locked.map((t) => (
                <div
                  key={t.label}
                  style={{
                    background: c.cardAlt, border: `1px dashed ${c.border}`,
                    borderLeft: `3px solid ${c.dim}`, borderRadius: 8,
                    padding: '14px 16px', opacity: 0.85,
                    display: 'flex', flexDirection: 'column', gap: 6,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600, color: c.muted }}>
                    <span style={{ marginRight: 6, opacity: 0.6 }}>&#x1F512;</span>
                    {t.label}
                  </span>
                  <span style={{ fontSize: 12, color: c.muted, lineHeight: 1.4 }}>{t.teaser}</span>
                  <button
                    onClick={() => navigate('/upgrade')}
                    style={{
                      marginTop: 6, alignSelf: 'flex-start',
                      background: 'transparent', color: t.accent,
                      border: `1px solid ${t.accent}`, padding: '3px 10px',
                      borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                    }}
                  >Add to your plan</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PillarLanding;
